import {
  DraftType,
  GenerationStatus,
  isDraftGenerating,
  type AgentFailureCode,
  type Draft
} from "../../../api/types/draft";
import { PacketGenerationStatus, type Packet } from "../../../api/types/packet";
import {
  fillProgressDetail,
  formFill,
  type FormFill
} from "../../casePrepUscisForms/fillProgress";
import type { CasePrepUscisFilledForm } from "../../casePrepUscisForms/types";
import { childrenByParent, subtreeIds } from "../files/treeRows";
import { PLAN_STATUS_LABEL, todoProgress } from "../plan/model";
import type { Artifact, CaseAgentPlan } from "../types";
import type { WsNode, WsNodeKind } from "./types";

export const RESEARCH_MEMO_SUBTYPE = "research_memo";

/** Normalised status shared by every workspace item, whatever its source. */
export type WorkspaceStatus =
  | "idle"
  | "waiting"
  | "running"
  | "ready"
  | "stale"
  | "failed";

export type WorkspaceSectionId =
  | "exhibit-list"
  | "drafts"
  | "packet"
  | "forms"
  | "research"
  | "plans";

export type WorkspaceItem = {
  key: string;
  artifact: Artifact;
  status: WorkspaceStatus;
  /** Secondary line: draft type, form number, page count, ... */
  detail?: string | null;
  /** What the run now going on is doing, as its agent last reported it. */
  activity?: string | null;
  /** When the run now going on it started (epoch ms), for "· 4 min". */
  runningSince?: number | null;
  updatedAt?: string | null;
  /** A draft's type: which letter it is. */
  draftType?: string;
  /** A form's fill: how many fields hold an answer, a running or failed fill. */
  fill?: FormFill;
  /** The forms the packet's last build printed, by form id. */
  packetForms?: string[];
};

export type WorkspaceSection = {
  id: WorkspaceSectionId;
  title: string;
  items: WorkspaceItem[];
  /** Short hint rendered when the section has no items. */
  empty: string;
};

const DRAFT_TYPE_LABEL: Record<DraftType, string> = {
  [DraftType.LEGAL_BRIEF]: "Legal Brief",
  [DraftType.SUPPORT_LETTER]: "Support Letter",
  [DraftType.RFE_RESPONSE]: "RFE Response",
  [DraftType.NOIR_NOID_RESPONSE]: "NOID/NOIR Response",
  [DraftType.EXPERT_LETTER]: "Expert Letter",
  [DraftType.EXHIBIT_LIST]: "Exhibit List",
  [DraftType.JOB_DESCRIPTION]: "Job Description",
  [DraftType.NARRATIVE_RECRUITMENT_REPORT]: "Recruitment Report",
  [DraftType.EMPLOYMENT_VERIFICATION_LETTER]: "Employment Verification",
  [DraftType.NEWSPAPER_AD]: "Newspaper Ad",
  [DraftType.SWA_JOB_ORDER]: "SWA Job Order",
  [DraftType.NOTICE_OF_FILING]: "Notice of Filing",
  [DraftType.PERSONAL_STATEMENT]: "Personal Statement",
  [DraftType.ASYLUM_AFFIDAVIT]: "Asylum Affidavit",
  [DraftType.HARDSHIP_STATEMENT_BENEFICIARY]:
    "Hardship Statement (Beneficiary)",
  [DraftType.HARDSHIP_STATEMENT_PETITIONER]: "Hardship Statement (Petitioner)",
  [DraftType.COVER_LETTER]: "Cover Letter",
  [DraftType.CERTIFIED_STATEMENT]: "Certified Statement",
  [DraftType.CUSTOM]: "Custom"
};

export const draftTypeLabel = (type: DraftType): string =>
  DRAFT_TYPE_LABEL[type] ?? type;

/**
 * The backend derives `display_status` (content reads ready whatever the
 * stored status; run state wins). The mapping below is for a response from a
 * backend that predates it.
 */
export const draftStatus = (draft: Draft): WorkspaceStatus => {
  if (draft.display_status) return draft.display_status;
  if (isDraftGenerating(draft)) return "running";
  switch (draft.status) {
    case GenerationStatus.GENERATED:
      return "ready";
    case GenerationStatus.NEEDS_UPDATE:
      return "stale";
    case GenerationStatus.FAILED:
      return "failed";
    case GenerationStatus.IN_PROGRESS:
      return "running";
    case GenerationStatus.WAITING_FOR_ANALYSIS:
      return "waiting";
    default:
      return "idle";
  }
};

export const packetStatus = (packet: Packet | null): WorkspaceStatus => {
  if (!packet) return "idle";
  if (packet.needs_update) return "stale";
  switch (packet.status) {
    case PacketGenerationStatus.GENERATING:
    case PacketGenerationStatus.IN_PROGRESS:
      return "running";
    case PacketGenerationStatus.GENERATED:
      return "ready";
    case PacketGenerationStatus.NEEDS_UPDATE:
      return "stale";
    case PacketGenerationStatus.FAILED:
      return "failed";
    default:
      return "idle";
  }
};

export const formStatus = (form: CasePrepUscisFilledForm): WorkspaceStatus => {
  switch (form.status) {
    case "generating":
      return form.run_stalled ? "failed" : "running";
    case "generated":
      return "ready";
    case "failed":
      return "failed";
    default:
      // A never-filled form whose fill failed early goes back to its old status.
      return form.last_run_error && formFill(form).state === "failed"
        ? "failed"
        : "idle";
  }
};

export const isResearchMemo = (draft: Draft): boolean =>
  draft.draft_type === DraftType.CUSTOM &&
  draft.visa_subtype === RESEARCH_MEMO_SUBTYPE;

export const draftTitle = (draft: Draft): string =>
  draft.title?.trim() || draftTypeLabel(draft.draft_type);

const byUpdatedDesc = (
  a: { updatedAt?: string | null },
  b: { updatedAt?: string | null }
) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");

const draftKindLabel = (draft: Draft): string => {
  if (!isResearchMemo(draft)) return draftTypeLabel(draft.draft_type);
  return draft.fact_key ? "Insight Analysis" : "Research Memo";
};

const UNREACHABLE =
  /cannot be reached|could not reach|dns|webhook host|connection (refused|reset)|nodename|name or service not known/i;
const TOO_LONG = /timed? ?out|ran out of time|deadline exceeded/i;
const TECHNICAL =
  /traceback|exception|error\b|errno|connectionpool|https?:\/\/|[{}[\]<>]|status code/i;

/** The label for each typed failure; mirrors the backend's titles. */
export const RUN_FAILURE_TITLES: Record<AgentFailureCode, string> = {
  no_changes_needed: "The revision changed nothing",
  review_locked: "The draft was waiting for review",
  citation_refused: "A citation was refused",
  context_too_large: "The case was too large for one run",
  contract_failed: "The draft did not meet its checks",
  mount_failed: "The case files could not be opened",
  no_document: "The run ended without a document",
  delivery_failed: "The draft could not be saved",
  missing_record: "The record lacks what the draft needs",
  bad_instruction: "The instructions conflict",
  timeout: "The run took too long",
  budget_exhausted: "The run ran out of steps",
  runtime_lost: "The drafting service restarted",
  cancelled: "The run was stopped",
  model_error: "The run failed"
};

/**
 * A run's failure reason as an attorney reads it. A typed `code` picks the
 * label; without one (older runs) the reason text is matched. The backend
 * records the runtime's own text ("could not start the drafting run:
 * HTTPSConnectionPool ..."); that stays available as a tooltip, never as the
 * label.
 */
export const friendlyRunFailure = (
  reason: string | null | undefined,
  code?: AgentFailureCode | string | null
): string => {
  if (code && code !== "model_error" && code in RUN_FAILURE_TITLES)
    return RUN_FAILURE_TITLES[code as AgentFailureCode];
  const text = reason?.trim() ?? "";
  if (!text) return "The run failed";
  if (UNREACHABLE.test(text)) return "Couldn't reach the drafting service";
  if (TOO_LONG.test(text)) return "The run took too long";
  if (/without (submitting|uploading|writing)/i.test(text))
    return "The run ended without a document";
  if (/could not start/i.test(text)) return "The run couldn't start";
  if (TECHNICAL.test(text) || text.length > 100) return "The run failed";
  const sentence = text.replace(/\.$/, "");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

const words = (text: string) => text.toLowerCase().split(/[\s/]+/);

/** "NOID/NOIR Response" under "NOIR Response" says nothing the title didn't. */
const echoes = (title: string, kind: string): boolean => {
  const named = new Set(words(kind));
  return words(title).every((w) => named.has(w));
};

/** What the draft's current run is doing, or null when none is going or it has not said. */
export const draftActivity = (draft: Draft): string | null =>
  draftStatus(draft) === "running"
    ? draft.display_activity?.activity?.trim() || null
    : null;

/**
 * The one-line detail under a draft's title. A failed draft says why in plain
 * words; a written draft whose latest run failed keeps its kind and says so,
 * since its document is still the one from before.
 */
export const draftDetail = (draft: Draft): string | null => {
  const failure =
    draft.display_status !== undefined
      ? draft.display_failure
      : draft.agent_failure_reason?.trim() || draft.agent_failure
      ? { ...draft.agent_failure, message: draft.agent_failure_reason }
      : null;
  const status = draftStatus(draft);
  if (status === "failed")
    return friendlyRunFailure(failure?.message, failure?.code);
  const activity = draftActivity(draft);
  if (activity) return activity;
  if (status === "waiting") {
    const names = Object.values(draft.waiting_labels ?? {});
    return names.length
      ? `Starts after ${names.join(", ")}`
      : "Starts after the analyses finish";
  }
  if (failure) return `${draftKindLabel(draft)} · last run failed`;
  const kind = draftKindLabel(draft);
  return echoes(draftTitle(draft), kind) ? null : kind;
};

/** When a draft's open run started (epoch ms), or null when it has no recorded start. */
export const runStartedAt = (
  draft: Pick<Draft, "display_activity" | "agent_runs">
): number | null => {
  const started =
    draft.display_activity?.started_at ??
    [...(draft.agent_runs ?? [])].reverse().find((r) => r.outcome === "running")
      ?.started_at;
  const ms = started ? Date.parse(started) : NaN;
  return Number.isNaN(ms) ? null : ms;
};

/** When the draft's current run started (epoch ms), or null when none is going. */
export const draftRunningSince = (draft: Draft): number | null =>
  draftStatus(draft) === "running" ? runStartedAt(draft) : null;

export const draftItem = (draft: Draft): WorkspaceItem => ({
  key: `draft:${draft.draft_id}`,
  artifact: { kind: "draft", id: draft.draft_id, title: draftTitle(draft) },
  status: draftStatus(draft),
  detail: draftDetail(draft),
  activity: draftActivity(draft),
  runningSince: draftRunningSince(draft),
  updatedAt: draft.updated_at ?? draft.created_at ?? null,
  draftType: String(draft.draft_type)
});

/**
 * "Edition 04/01/24" from a catalog edition date, matching the footer the
 * agency prints on the form. Accepts `YYYY-MM-DD`, or `YYYY-MM` for agencies
 * that only stamp a month (rendered "Edition 04/24"). Null when unparseable.
 */
export const formatEdition = (
  iso: string | null | undefined
): string | null => {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(iso?.trim() ?? "");
  if (!m) return null;
  const [, year, month, day] = m;
  const yy = year.slice(2);
  return `Edition ${day ? `${month}/${day}/${yy}` : `${month}/${yy}`}`;
};

/**
 * The one-line detail under a form: a running fill's progress, else its
 * edition, or its title when unknown.
 */
export const formDetail = (form: CasePrepUscisFilledForm): string => {
  const progress = fillProgressDetail(form);
  if (progress) return `Filling · ${progress}`;
  if (form.status === "generating") return "Filling…";
  return formatEdition(form.edition_date) ?? form.form_title;
};

/** "G-28", or "G-28 (Beneficiary)" for one of the case's copies of a form. */
export const formName = (
  form: Pick<CasePrepUscisFilledForm, "form_number" | "label">
): string => {
  const label = form.label?.trim();
  return label ? `${form.form_number} (${label})` : form.form_number;
};

export const formItem = (form: CasePrepUscisFilledForm): WorkspaceItem => ({
  key: `form:${form.case_extend_uscis_filled_form_id}`,
  artifact: {
    kind: "form",
    id: form.case_extend_uscis_filled_form_id,
    title: formName(form)
  },
  status: formStatus(form),
  detail: formDetail(form),
  updatedAt: typeof form.updated_at === "string" ? form.updated_at : null,
  fill: { ...formFill(form), formNumber: formName(form) }
});

export const packetItem = (packet: Packet | null): WorkspaceItem | null => {
  if (!packet) return null;
  const pages = packet.total_pages ? `${packet.total_pages} pages` : null;
  return {
    key: `packet:${packet.packet_id}`,
    artifact: { kind: "packet", id: packet.packet_id, title: "Filing Packet" },
    status: packetStatus(packet),
    detail: pages,
    updatedAt: packet.generated_at ?? packet.updated_at ?? null,
    packetForms: (packet.front_matter_report?.parts ?? []).flatMap((p) =>
      p.form_id ? [p.form_id] : []
    )
  };
};

export const planStatus = (plan: CaseAgentPlan): WorkspaceStatus => {
  switch (plan.status) {
    case "building":
      return "running";
    case "complete":
      return "ready";
    default:
      return "idle";
  }
};

/** A plan saved to the workspace: the same document the plan pane follows. */
export const planItem = (plan: CaseAgentPlan): WorkspaceItem => ({
  key: `plan:${plan.id}`,
  artifact: { kind: "plan", id: plan.id, title: plan.title },
  status: planStatus(plan),
  detail: `${PLAN_STATUS_LABEL[plan.status]} · ${
    todoProgress(plan.todos).label
  }`,
  updatedAt: plan.updated_at ?? plan.created_at ?? null
});

export type WorkspaceSources = {
  drafts: Draft[];
  packet: Packet | null;
  forms: CasePrepUscisFilledForm[];
  /** Plans the attorney saved to the workspace; absent where plans don't apply. */
  plans?: CaseAgentPlan[];
};

/** Group the raw sources into the sections the tree renders, newest first. */
export const buildSections = ({
  drafts,
  packet,
  forms,
  plans = []
}: WorkspaceSources): WorkspaceSection[] => {
  const live = drafts.filter(
    (d) => !(d.metadata as { deleted?: boolean } | undefined)?.deleted
  );
  const exhibitLists = live.filter(
    (d) => d.draft_type === DraftType.EXHIBIT_LIST
  );
  const research = live.filter(isResearchMemo);
  const others = live.filter(
    (d) => d.draft_type !== DraftType.EXHIBIT_LIST && !isResearchMemo(d)
  );
  const packetEntry = packetItem(packet);
  // Same order and titles as the default folders the server seeds.
  return [
    {
      id: "drafts",
      title: "Drafts",
      items: others.map(draftItem).sort(byUpdatedDesc),
      empty: "No letters or briefs yet."
    },
    {
      id: "forms",
      title: "Forms",
      items: forms.map(formItem).sort(byUpdatedDesc),
      empty: "No USCIS forms yet."
    },
    {
      id: "exhibit-list",
      title: "Exhibit List",
      items: exhibitLists.map(draftItem).sort(byUpdatedDesc),
      empty: "Ask the agent to generate the exhibit list."
    },
    {
      id: "packet",
      title: "Packet",
      items: packetEntry ? [packetEntry] : [],
      empty: "No packet yet."
    },
    {
      id: "research",
      title: "Research",
      items: research.map(draftItem).sort(byUpdatedDesc),
      empty: "No research memos yet."
    },
    {
      id: "plans",
      title: "Plans",
      items: plans.map(planItem).sort(byUpdatedDesc),
      empty: "Save a plan from the agent to keep it here."
    }
  ];
};

/** True when anything in the workspace is still being produced. */
export const anyRunning = (sections: WorkspaceSection[]): boolean =>
  sections.some((s) => s.items.some((i) => i.status === "running"));

// ---------------------------------------------------------------------------
// The tree: workspace nodes joined to the artifacts they reference.
// ---------------------------------------------------------------------------

/** One rendered row of the workspace tree. `item` is null for folders. */
export type WsRow = {
  node: WsNode;
  item: WorkspaceItem | null;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
};

/** `kind:ref_id` -> item, for joining nodes to what they point at. */
export const indexItems = ({
  drafts,
  packet,
  forms,
  plans = []
}: WorkspaceSources): Map<string, WorkspaceItem> => {
  const out = new Map<string, WorkspaceItem>();
  drafts
    .filter((d) => !(d.metadata as { deleted?: boolean } | undefined)?.deleted)
    .forEach((d) => out.set(`draft:${d.draft_id}`, draftItem(d)));
  forms.forEach((f) =>
    out.set(`form:${f.case_extend_uscis_filled_form_id}`, formItem(f))
  );
  const p = packetItem(packet);
  if (p && packet) out.set(`packet:${packet.packet_id}`, p);
  plans.forEach((plan) => out.set(`plan:${plan.id}`, planItem(plan)));
  return out;
};

export const itemKeyFor = (node: WsNode): string | null =>
  node.kind === "folder" || !node.ref_id ? null : `${node.kind}:${node.ref_id}`;

/**
 * Flatten the tree into rows, joining each item node to its artifact. An item
 * whose artifact is not loaded (deleted elsewhere, or a reference the server
 * has not yet dropped) is hidden rather than shown as a blank row.
 */
export const buildWorkspaceRows = (
  nodes: WsNode[],
  sources: WorkspaceSources,
  expanded: ReadonlySet<string>
): WsRow[] => {
  const items = indexItems(sources);
  const grouped = childrenByParent(nodes);
  const rows: WsRow[] = [];
  const walk = (parentId: string | null, depth: number) => {
    (grouped.get(parentId) ?? []).forEach((node) => {
      let item: WorkspaceItem | null = null;
      if (node.kind !== "folder") {
        const key = itemKeyFor(node);
        item = key ? items.get(key) ?? null : null;
        if (!item) return;
      }
      const kids = grouped.get(node.id) ?? [];
      const isOpen = node.kind === "folder" && expanded.has(node.id);
      rows.push({
        node,
        item,
        depth,
        hasChildren: kids.length > 0,
        expanded: isOpen
      });
      if (isOpen) walk(node.id, depth + 1);
    });
  };
  walk(null, 0);
  return rows;
};

/** True when any of `ids`, or anything inside them, is a node of `kind`. */
export const subtreeContainsKind = (
  nodes: WsNode[],
  ids: string[],
  kind: WsNodeKind
): boolean => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return ids.some((id) =>
    [...subtreeIds(nodes, id)].some((x) => byId.get(x)?.kind === kind)
  );
};

/** The packet is regenerated, never deleted; a selection holding it cannot be. */
export const containsPacket = (nodes: WsNode[], ids: string[]): boolean =>
  subtreeContainsKind(nodes, ids, "packet");

/** `/`-joined titles for every folder, for "Move to" menus. */
export const folderPaths = (nodes: WsNode[]): Map<string, string> => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out = new Map<string, string>();
  nodes.forEach((node) => {
    if (node.kind !== "folder" || node.is_deleted) return;
    const segments: string[] = [];
    let cursor: WsNode | undefined = node;
    const seen = new Set<string>();
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      segments.unshift(cursor.name);
      cursor = cursor.parent_id ? byId.get(cursor.parent_id) : undefined;
    }
    out.set(node.id, segments.join("/"));
  });
  return out;
};

/** The title a row shows: the folder's name or the artifact's title. */
export const rowTitle = (row: WsRow): string =>
  row.node.kind === "folder" ? row.node.name : row.item?.artifact.title ?? "";
